import create from 'zustand';
import cn from '@/utils/cn';
import I18n from '@/modules/i18n';

export const BUTTON_CLASSES = cn(
  'tw-h-10 tw-gap-2 tw-px-2.5 tw-py-2 tw-leading-7',
  'tw-flex tw-items-center tw-justify-evenly',
  'shadow-soft tw-z-[2000]',
  'tw-text-white tw-text-lg tw-font-[inherit]',
  'tw-cursor-pointer tw-transition-all',
  'tw-border-none tw-rounded',
  'tw-bg-dark-orange focus:tw-text-white focus:tw-bg-[#ab3b01] focus:tw-border-dark-orange',
  'hover:tw-text-white hover:tw-bg-[#ab3b01] hover:tw-border-dark-orange',
);

export const HEADER_BUTTON_CLASSES = cn(
  'tw-w-7 tw-h-7 tw-box-border tw-rounded-full tw-shadow-none tw-outline-none tw-border-none',
  'tw-fill-tci-purple tw-p-2 tw-inline-flex tw-items-center tw-justify-center tw-cursor-pointer',
  'tw-bg-light-grey hover:tw-bg-tci-purple hover:tw-fill-light-grey tw-transition-all',
  'focus:tw-text-light-grey hover:tw-text-light-grey',
  'focus:tw-bg-tci-purple focus:tw-fill-light-grey focus:tw-outline-none',
);

export type PlanningContext = 'lesson' | 'unit';

export type Resource = {
  id: number;
  html: string | null;
  name: string;
  title: string;
};

/**
 * dialog.standard and title build dynamic keys for the unit context, so unit_planning_dialog and
 * unit_planning must exist in config/locales. The lesson context uses teachers_guide for both.
 */
export const TRANSLATIONS = {
  dialog: {
    close: I18n.t('close_dialog') as string,
    collapse: I18n.t('exit_full_screen') as string,
    expand: I18n.t('enter_full_screen') as string,
    standard: (context: PlanningContext) => (context === 'lesson'
      ? I18n.t('teachers_guide') : I18n.t(`${context}_planning_dialog`)) as string,
  },
  materials: I18n.t('materials') as string,
  printMessage: I18n.t('print_overview') as string,
  title: (context: PlanningContext) => (context === 'lesson'
    ? I18n.t('teachers_guide') : I18n.t(`${context}_planning`)) as string,
  visibilityMessage: I18n.t('not_visible_to_students') as string,
} as const;

interface UsePlanningDrawer {
  close: () => void;
  collapse: () => void;
  expand: () => void;
  isExpanded: boolean;
  isOpen: boolean;
  open: () => void;
  toggleExpanded: () => void;
  toggleOpen: () => void;
}

export const usePlanningDrawer = create<UsePlanningDrawer>(set => ({
  close: () => set({ isExpanded: false, isOpen: false }),
  collapse: () => set({ isExpanded: false }),
  expand: () => set({ isExpanded: true }),
  isExpanded: false,
  isOpen: false,
  open: () => set({ isOpen: true }),
  toggleExpanded: () => set(prev => ({ ...prev, isExpanded: !prev.isExpanded })),
  toggleOpen: () => set((prev) => {
    const nextIsOpen = !prev.isOpen;
    // If the drawer is open, keep the expanded state. Otherwise, collapse the drawer.
    const nextExpanded = nextIsOpen ? prev.isExpanded : false;

    return { isExpanded: nextExpanded, isOpen: nextIsOpen };
  }),
}));
